import {
    AnimationEasing,
    clamp01,
    createCatmullRomPath,
    createCubicBezier,
    createEasingFunction,
    createMonotoneCubic,
    createTrackSampler,
    getConvexPolygonSignedDistance,
    getDistance,
    getTrapezoidalProgress,
    IKeyframeTrack,
    invertMonotonic,
    IPoint,
    IRect,
    ITrapezoidalProfile,
    lerp,
    roundTo
} from "@pg-mono/animation-utils";

export interface IPlaneWaypoint {
    x: number;
    y: number;
    rotate: number;
}

export interface IPlaneApproach {
    start: number;
    rotate: number;
}

export interface IPlaneFlightLeg extends ITrapezoidalProfile {
    start: number;
    end: number;
    waypoints: IPlaneWaypoint[];
    approach?: IPlaneApproach;
}

export interface IPlaneExit {
    delayMs: number;
    durationMs: number;
    distance: number;
    scale: number;
    ease: AnimationEasing;
}

export interface IPlaneMotion {
    legs: IPlaneFlightLeg[];
    scale: IKeyframeTrack;
    opacity: IKeyframeTrack;
    exit?: IPlaneExit;
}

export interface IPlanePose {
    x: number;
    y: number;
    rotate: number;
    scale: number;
    opacity: number;
}

export interface IPlaneKeyframe extends Keyframe {
    offset: number;
    transform: string;
    opacity: number;
}

export interface IPlaneSilhouette {
    box: IRect;
    hull: IPoint[];
}

export interface ITrailRevealOptions {
    clearance: number;
    hiddenOpacity: number;
    stepMs?: number;
}

export const PLANE_KEYFRAME_STEP_MS = 10;

const PROJECTION_SAMPLES = 1000;
const TRAIL_STEP_MS = 1;
const APPROACH_EASE: AnimationEasing = [0.5, 0, 0.5, 1];

interface IFlightPose {
    x: number;
    y: number;
    rotate: number;
}

const toFlightPose = (waypoint: IPlaneWaypoint, rotate = waypoint.rotate): IFlightPose => ({x: waypoint.x, y: waypoint.y, rotate});

export const createPlanePoseSampler = (motion: IPlaneMotion): ((time: number) => IPlanePose) => {
    const legs = motion.legs.map((leg) => {
        const path = createCatmullRomPath(leg.waypoints);

        return {
            leg,
            path,
            rotationAt: createMonotoneCubic(
                path.waypointFractions,
                leg.waypoints.map((waypoint) => waypoint.rotate)
            )
        };
    });
    const scaleAt = createTrackSampler(motion.scale);
    const opacityAt = createTrackSampler(motion.opacity);
    const approachEase = createCubicBezier(APPROACH_EASE);
    const lastLeg = motion.legs[motion.legs.length - 1];

    const getFlightPose = (time: number): IFlightPose => {
        for (let index = 0; index < legs.length; index++) {
            const {leg, path, rotationAt} = legs[index];
            const {waypoints, approach} = leg;
            const next = legs[index + 1]?.leg;
            const visibleFrom = approach?.start ?? leg.start;
            const handover = next ? (leg.end + (next.approach?.start ?? next.start)) / 2 : Infinity;

            if (time > handover) {
                continue;
            }

            if (time <= visibleFrom) {
                return toFlightPose(waypoints[0], approach?.rotate);
            }

            if (approach && time < leg.start) {
                const progress = approachEase((time - approach.start) / (leg.start - approach.start));

                return toFlightPose(waypoints[0], lerp(approach.rotate, waypoints[0].rotate, progress));
            }

            if (time >= leg.end) {
                return toFlightPose(waypoints[waypoints.length - 1]);
            }

            const fraction = getTrapezoidalProgress((time - leg.start) / (leg.end - leg.start), leg);
            const point = path.pointAt(fraction);

            return {x: point.x, y: point.y, rotate: rotationAt(fraction)};
        }

        return toFlightPose(lastLeg.waypoints[lastLeg.waypoints.length - 1]);
    };

    return (time) => ({...getFlightPose(time), scale: scaleAt(time), opacity: opacityAt(time)});
};

export const toPlaneTransform = (pose: Pick<IPlanePose, "x" | "y" | "rotate" | "scale">): string =>
    `translate(${roundTo(pose.x, 3)}px, ${roundTo(pose.y, 3)}px) rotate(${roundTo(pose.rotate, 3)}deg) scale(${roundTo(pose.scale, 4)})`;

export const getPlaneAnimationDurationMs = (motion: IPlaneMotion, flightDurationMs: number): number =>
    flightDurationMs + (motion.exit ? motion.exit.delayMs + motion.exit.durationMs : 0);

export const createPlaneExitSampler = (landing: IPlanePose, exit: IPlaneExit): ((progress: number) => IPlanePose) => {
    const ease = createEasingFunction(exit.ease);
    const heading = (landing.rotate * Math.PI) / 180;

    return (progress) => {
        const eased = ease(clamp01(progress));

        return {
            x: landing.x + Math.cos(heading) * exit.distance * eased,
            y: landing.y + Math.sin(heading) * exit.distance * eased,
            rotate: landing.rotate,
            scale: lerp(landing.scale, exit.scale, eased),
            opacity: lerp(landing.opacity, 0, eased)
        };
    };
};

const getFlightOffsets = (motion: IPlaneMotion, durationMs: number, stepMs: number): number[] => {
    const offsets = new Set<number>([0, 1]);
    const steps = Math.ceil(durationMs / stepMs);

    for (let step = 0; step <= steps; step++) {
        offsets.add(clamp01((step * stepMs) / durationMs));
    }

    motion.legs.forEach((leg) => {
        offsets.add(leg.start);
        offsets.add(leg.end);

        if (leg.approach) {
            offsets.add(leg.approach.start);
        }
    });
    [...motion.scale.times, ...motion.opacity.times].forEach((time) => offsets.add(time));

    return [...new Set([...offsets].map((offset) => roundTo(offset, 6)))].sort((a, b) => a - b);
};

const toPlaneKeyframe = (offset: number, pose: IPlanePose): IPlaneKeyframe => ({
    offset,
    transform: toPlaneTransform(pose),
    opacity: roundTo(pose.opacity, 4)
});

export const buildPlaneKeyframes = (motion: IPlaneMotion, flightDurationMs: number, stepMs = PLANE_KEYFRAME_STEP_MS): IPlaneKeyframe[] => {
    const sample = createPlanePoseSampler(motion);
    const flightOffsets = getFlightOffsets(motion, flightDurationMs, stepMs);
    const {exit} = motion;

    if (!exit) {
        return flightOffsets.map((offset) => toPlaneKeyframe(offset, sample(offset)));
    }

    const totalMs = getPlaneAnimationDurationMs(motion, flightDurationMs);
    const toOffset = (timeMs: number) => roundTo(timeMs / totalMs, 6);
    const exitStartMs = flightDurationMs + exit.delayMs;
    const exitPoseAt = createPlaneExitSampler(sample(1), exit);
    const exitSteps = Math.ceil(exit.durationMs / stepMs);
    const flightKeyframes = flightOffsets.map((offset) => toPlaneKeyframe(toOffset(offset * flightDurationMs), sample(offset)));
    const exitKeyframes: IPlaneKeyframe[] = [];

    for (let step = 0; step <= exitSteps; step++) {
        const progress = Math.min(1, (step * stepMs) / exit.durationMs);

        exitKeyframes.push(toPlaneKeyframe(toOffset(exitStartMs + progress * exit.durationMs), exitPoseAt(progress)));
    }

    return [...flightKeyframes, ...exitKeyframes].filter((keyframe, index, keyframes) => index === 0 || keyframe.offset > keyframes[index - 1].offset);
};

export const createPlaneTimelineSampler = (motion: IPlaneMotion, flightDurationMs: number): ((timeMs: number) => IPlanePose) => {
    const sample = createPlanePoseSampler(motion);
    const {exit} = motion;

    if (!exit) {
        return (timeMs) => sample(clamp01(timeMs / flightDurationMs));
    }

    const exitPoseAt = createPlaneExitSampler(sample(1), exit);
    const exitStartMs = flightDurationMs + exit.delayMs;

    return (timeMs) => {
        if (timeMs <= flightDurationMs) {
            return sample(clamp01(timeMs / flightDurationMs));
        }

        return exitPoseAt((timeMs - exitStartMs) / exit.durationMs);
    };
};

export const getPlaneSilhouettePolygon = (silhouette: IPlaneSilhouette, pose: IPlanePose): IPoint[] => {
    const {box, hull} = silhouette;
    const angle = (pose.rotate * Math.PI) / 180;
    const cos = Math.cos(angle) * pose.scale;
    const sin = Math.sin(angle) * pose.scale;
    const centerX = box.left + box.width / 2 + pose.x;
    const centerY = box.top + box.height / 2 + pose.y;

    return hull.map((vertex) => {
        const localX = (vertex.x - 0.5) * box.width;
        const localY = (vertex.y - 0.5) * box.height;

        return {x: centerX + localX * cos - localY * sin, y: centerY + localX * sin + localY * cos};
    });
};

export const getTrailRevealTimesMs = (
    motion: IPlaneMotion,
    flightDurationMs: number,
    silhouette: IPlaneSilhouette,
    marks: IPoint[],
    options: ITrailRevealOptions
): number[] => {
    const {clearance, hiddenOpacity, stepMs = TRAIL_STEP_MS} = options;
    const poseAt = createPlaneTimelineSampler(motion, flightDurationMs);
    const totalMs = getPlaneAnimationDurationMs(motion, flightDurationMs);
    const restCenter = {x: silhouette.box.left + silhouette.box.width / 2, y: silhouette.box.top + silhouette.box.height / 2};
    const legPaths = motion.legs.map((leg) => ({leg, path: createCatmullRomPath(leg.waypoints)}));

    return marks.map((mark) => {
        const target = {x: mark.x - restCenter.x, y: mark.y - restCenter.y};
        const nearest = legPaths.reduce(
            (best, {leg, path}) => {
                for (let step = 0; step <= PROJECTION_SAMPLES; step++) {
                    const fraction = step / PROJECTION_SAMPLES;
                    const gap = getDistance(path.pointAt(fraction), target);

                    if (gap < best.gap) {
                        best = {leg, fraction, gap};
                    }
                }

                return best;
            },
            {leg: motion.legs[0], fraction: 0, gap: Infinity}
        );
        const {leg, fraction} = nearest;
        const passedAtMs =
            (leg.start + invertMonotonic((progress) => getTrapezoidalProgress(progress, leg), fraction) * (leg.end - leg.start)) * flightDurationMs;

        for (let timeMs = Math.ceil(passedAtMs / stepMs) * stepMs; timeMs < totalMs; timeMs += stepMs) {
            const pose = poseAt(timeMs);

            if (pose.opacity <= hiddenOpacity || getConvexPolygonSignedDistance(getPlaneSilhouettePolygon(silhouette, pose), mark) >= clearance) {
                return timeMs;
            }
        }

        return totalMs;
    });
};
import {
    AnimationEasing,
    clamp01,
    createCatmullRomPath,
    createCubicBezier,
    createEasingFunction,
    createMonotoneCubic,
    createTrackSampler,
    getConvexPolygonSignedDistance,
    getDistance,
    getTrapezoidalProgress,
    IKeyframeTrack,
    invertMonotonic,
    IPoint,
    IRect,
    ITrapezoidalProfile,
    lerp,
    roundTo
} from "@pg-mono/animation-utils";

export interface IPlaneWaypoint {
    x: number;
    y: number;
    rotate: number;
}

export interface IPlaneApproach {
    start: number;
    rotate: number;
}

export interface IPlaneFlightLeg extends ITrapezoidalProfile {
    start: number;
    end: number;
    waypoints: IPlaneWaypoint[];
    approach?: IPlaneApproach;
}

export interface IPlaneExit {
    delayMs: number;
    durationMs: number;
    distance: number;
    scale: number;
    ease: AnimationEasing;
}

export interface IPlaneMotion {
    legs: IPlaneFlightLeg[];
    scale: IKeyframeTrack;
    opacity: IKeyframeTrack;
    exit?: IPlaneExit;
}

export interface IPlanePose {
    x: number;
    y: number;
    rotate: number;
    scale: number;
    opacity: number;
}

export interface IPlaneKeyframe extends Keyframe {
    offset: number;
    transform: string;
    opacity: number;
}

export interface IPlaneSilhouette {
    box: IRect;
    hull: IPoint[];
}

export interface ITrailRevealOptions {
    clearance: number;
    hiddenOpacity: number;
    stepMs?: number;
}

export const PLANE_KEYFRAME_STEP_MS = 10;

const PROJECTION_SAMPLES = 1000;
const TRAIL_STEP_MS = 1;
const APPROACH_EASE: AnimationEasing = [0.5, 0, 0.5, 1];

interface IFlightPose {
    x: number;
    y: number;
    rotate: number;
}

const toFlightPose = (waypoint: IPlaneWaypoint, rotate = waypoint.rotate): IFlightPose => ({x: waypoint.x, y: waypoint.y, rotate});

export const createPlanePoseSampler = (motion: IPlaneMotion): ((time: number) => IPlanePose) => {
    const legs = motion.legs.map((leg) => {
        const path = createCatmullRomPath(leg.waypoints);

        return {
            leg,
            path,
            rotationAt: createMonotoneCubic(
                path.waypointFractions,
                leg.waypoints.map((waypoint) => waypoint.rotate)
            )
        };
    });
    const scaleAt = createTrackSampler(motion.scale);
    const opacityAt = createTrackSampler(motion.opacity);
    const approachEase = createCubicBezier(APPROACH_EASE);
    const lastLeg = motion.legs[motion.legs.length - 1];

    const getFlightPose = (time: number): IFlightPose => {
        for (let index = 0; index < legs.length; index++) {
            const {leg, path, rotationAt} = legs[index];
            const {waypoints, approach} = leg;
            const next = legs[index + 1]?.leg;
            const visibleFrom = approach?.start ?? leg.start;
            const handover = next ? (leg.end + (next.approach?.start ?? next.start)) / 2 : Infinity;

            if (time > handover) {
                continue;
            }

            if (time <= visibleFrom) {
                return toFlightPose(waypoints[0], approach?.rotate);
            }

            if (approach && time < leg.start) {
                const progress = approachEase((time - approach.start) / (leg.start - approach.start));

                return toFlightPose(waypoints[0], lerp(approach.rotate, waypoints[0].rotate, progress));
            }

            if (time >= leg.end) {
                return toFlightPose(waypoints[waypoints.length - 1]);
            }

            const fraction = getTrapezoidalProgress((time - leg.start) / (leg.end - leg.start), leg);
            const point = path.pointAt(fraction);

            return {x: point.x, y: point.y, rotate: rotationAt(fraction)};
        }

        return toFlightPose(lastLeg.waypoints[lastLeg.waypoints.length - 1]);
    };

    return (time) => ({...getFlightPose(time), scale: scaleAt(time), opacity: opacityAt(time)});
};

export const toPlaneTransform = (pose: Pick<IPlanePose, "x" | "y" | "rotate" | "scale">): string =>
    `translate(${roundTo(pose.x, 3)}px, ${roundTo(pose.y, 3)}px) rotate(${roundTo(pose.rotate, 3)}deg) scale(${roundTo(pose.scale, 4)})`;

export const getPlaneAnimationDurationMs = (motion: IPlaneMotion, flightDurationMs: number): number =>
    flightDurationMs + (motion.exit ? motion.exit.delayMs + motion.exit.durationMs : 0);

export const createPlaneExitSampler = (landing: IPlanePose, exit: IPlaneExit): ((progress: number) => IPlanePose) => {
    const ease = createEasingFunction(exit.ease);
    const heading = (landing.rotate * Math.PI) / 180;

    return (progress) => {
        const eased = ease(clamp01(progress));

        return {
            x: landing.x + Math.cos(heading) * exit.distance * eased,
            y: landing.y + Math.sin(heading) * exit.distance * eased,
            rotate: landing.rotate,
            scale: lerp(landing.scale, exit.scale, eased),
            opacity: lerp(landing.opacity, 0, eased)
        };
    };
};

const getFlightOffsets = (motion: IPlaneMotion, durationMs: number, stepMs: number): number[] => {
    const offsets = new Set<number>([0, 1]);
    const steps = Math.ceil(durationMs / stepMs);

    for (let step = 0; step <= steps; step++) {
        offsets.add(clamp01((step * stepMs) / durationMs));
    }

    motion.legs.forEach((leg) => {
        offsets.add(leg.start);
        offsets.add(leg.end);

        if (leg.approach) {
            offsets.add(leg.approach.start);
        }
    });
    [...motion.scale.times, ...motion.opacity.times].forEach((time) => offsets.add(time));

    return [...new Set([...offsets].map((offset) => roundTo(offset, 6)))].sort((a, b) => a - b);
};

const toPlaneKeyframe = (offset: number, pose: IPlanePose): IPlaneKeyframe => ({
    offset,
    transform: toPlaneTransform(pose),
    opacity: roundTo(pose.opacity, 4)
});

export const buildPlaneKeyframes = (motion: IPlaneMotion, flightDurationMs: number, stepMs = PLANE_KEYFRAME_STEP_MS): IPlaneKeyframe[] => {
    const sample = createPlanePoseSampler(motion);
    const flightOffsets = getFlightOffsets(motion, flightDurationMs, stepMs);
    const {exit} = motion;

    if (!exit) {
        return flightOffsets.map((offset) => toPlaneKeyframe(offset, sample(offset)));
    }

    const totalMs = getPlaneAnimationDurationMs(motion, flightDurationMs);
    const toOffset = (timeMs: number) => roundTo(timeMs / totalMs, 6);
    const exitStartMs = flightDurationMs + exit.delayMs;
    const exitPoseAt = createPlaneExitSampler(sample(1), exit);
    const exitSteps = Math.ceil(exit.durationMs / stepMs);
    const flightKeyframes = flightOffsets.map((offset) => toPlaneKeyframe(toOffset(offset * flightDurationMs), sample(offset)));
    const exitKeyframes: IPlaneKeyframe[] = [];

    for (let step = 0; step <= exitSteps; step++) {
        const progress = Math.min(1, (step * stepMs) / exit.durationMs);

        exitKeyframes.push(toPlaneKeyframe(toOffset(exitStartMs + progress * exit.durationMs), exitPoseAt(progress)));
    }

    return [...flightKeyframes, ...exitKeyframes].filter((keyframe, index, keyframes) => index === 0 || keyframe.offset > keyframes[index - 1].offset);
};

export const createPlaneTimelineSampler = (motion: IPlaneMotion, flightDurationMs: number): ((timeMs: number) => IPlanePose) => {
    const sample = createPlanePoseSampler(motion);
    const {exit} = motion;

    if (!exit) {
        return (timeMs) => sample(clamp01(timeMs / flightDurationMs));
    }

    const exitPoseAt = createPlaneExitSampler(sample(1), exit);
    const exitStartMs = flightDurationMs + exit.delayMs;

    return (timeMs) => {
        if (timeMs <= flightDurationMs) {
            return sample(clamp01(timeMs / flightDurationMs));
        }

        return exitPoseAt((timeMs - exitStartMs) / exit.durationMs);
    };
};

export const getPlaneSilhouettePolygon = (silhouette: IPlaneSilhouette, pose: IPlanePose): IPoint[] => {
    const {box, hull} = silhouette;
    const angle = (pose.rotate * Math.PI) / 180;
    const cos = Math.cos(angle) * pose.scale;
    const sin = Math.sin(angle) * pose.scale;
    const centerX = box.left + box.width / 2 + pose.x;
    const centerY = box.top + box.height / 2 + pose.y;

    return hull.map((vertex) => {
        const localX = (vertex.x - 0.5) * box.width;
        const localY = (vertex.y - 0.5) * box.height;

        return {x: centerX + localX * cos - localY * sin, y: centerY + localX * sin + localY * cos};
    });
};

export const getTrailRevealTimesMs = (
    motion: IPlaneMotion,
    flightDurationMs: number,
    silhouette: IPlaneSilhouette,
    marks: IPoint[],
    options: ITrailRevealOptions
): number[] => {
    const {clearance, hiddenOpacity, stepMs = TRAIL_STEP_MS} = options;
    const poseAt = createPlaneTimelineSampler(motion, flightDurationMs);
    const totalMs = getPlaneAnimationDurationMs(motion, flightDurationMs);
    const restCenter = {x: silhouette.box.left + silhouette.box.width / 2, y: silhouette.box.top + silhouette.box.height / 2};
    const legPaths = motion.legs.map((leg) => ({leg, path: createCatmullRomPath(leg.waypoints)}));

    return marks.map((mark) => {
        const target = {x: mark.x - restCenter.x, y: mark.y - restCenter.y};
        const nearest = legPaths.reduce(
            (best, {leg, path}) => {
                for (let step = 0; step <= PROJECTION_SAMPLES; step++) {
                    const fraction = step / PROJECTION_SAMPLES;
                    const gap = getDistance(path.pointAt(fraction), target);

                    if (gap < best.gap) {
                        best = {leg, fraction, gap};
                    }
                }

                return best;
            },
            {leg: motion.legs[0], fraction: 0, gap: Infinity}
        );
        const {leg, fraction} = nearest;
        const passedAtMs =
            (leg.start + invertMonotonic((progress) => getTrapezoidalProgress(progress, leg), fraction) * (leg.end - leg.start)) * flightDurationMs;

        for (let timeMs = Math.ceil(passedAtMs / stepMs) * stepMs; timeMs < totalMs; timeMs += stepMs) {
            const pose = poseAt(timeMs);

            if (pose.opacity <= hiddenOpacity || getConvexPolygonSignedDistance(getPlaneSilhouettePolygon(silhouette, pose), mark) >= clearance) {
                return timeMs;
            }
        }

        return totalMs;
    });
};
